// Regional pricing resolver — no 'server-only' restriction.
// Safe to import in client components, server components, and API routes.
// This is the single source of truth for local-currency pricing display.
// It is intentionally separate from entitlements (src/lib/billing/entitlements.ts).
// Region affects: displayed currency, displayed price, tax wording, checkout metadata.
// Region never affects: plan limits, feature access, or entitlements.

export type PricingRegion = 'ZA' | 'UK' | 'EU' | 'AU' | 'NZ'

export const PRICING_REGIONS: PricingRegion[] = ['ZA', 'UK', 'EU', 'AU', 'NZ']

export type RegionConfig = {
  region: PricingRegion
  label: string
  flag: string
  currencyCode: string
  currencySymbol: string
  taxLabel: string
  taxShort: string
  locale: string
}

export const REGION_CONFIGS: Record<PricingRegion, RegionConfig> = {
  ZA: { region: 'ZA', label: 'South Africa',   flag: '🇿🇦', currencyCode: 'ZAR', currencySymbol: 'R',    taxLabel: 'VAT (15%)',  taxShort: 'VAT', locale: 'en-ZA' },
  UK: { region: 'UK', label: 'United Kingdom',  flag: '🇬🇧', currencyCode: 'GBP', currencySymbol: '£',    taxLabel: 'VAT (20%)',  taxShort: 'VAT', locale: 'en-GB' },
  EU: { region: 'EU', label: 'Europe',           flag: '🇪🇺', currencyCode: 'EUR', currencySymbol: '€',    taxLabel: 'VAT',        taxShort: 'VAT', locale: 'de-DE' },
  AU: { region: 'AU', label: 'Australia',        flag: '🇦🇺', currencyCode: 'AUD', currencySymbol: 'A$',   taxLabel: 'GST (10%)',  taxShort: 'GST', locale: 'en-AU' },
  NZ: { region: 'NZ', label: 'New Zealand',      flag: '🇳🇿', currencyCode: 'NZD', currencySymbol: 'NZ$',  taxLabel: 'GST (15%)',  taxShort: 'GST', locale: 'en-NZ' },
}

export type PlanPrices = {
  free:       number
  solo:       number
  contractor: number
  crew:       number
}

// Monthly prices in local currency (excl. tax).
// Prices are set per-region to be locally reasonable, not mechanically converted.
export const REGION_PLAN_PRICES: Record<PricingRegion, PlanPrices> = {
  ZA: { free: 0, solo: 299,  contractor: 599,  crew: 999  },
  UK: { free: 0, solo: 12,   contractor: 24,   crew: 48   },
  EU: { free: 0, solo: 13,   contractor: 26,   crew: 52   },
  AU: { free: 0, solo: 19,   contractor: 39,   crew: 78   },
  NZ: { free: 0, solo: 22,   contractor: 44,   crew: 88   },
}

// ISO country code → canonical PricingRegion
const COUNTRY_CODE_MAP: Record<string, PricingRegion> = {
  // South Africa
  ZA: 'ZA', ZAF: 'ZA',
  // United Kingdom
  GB: 'UK', UK: 'UK',
  // New Zealand
  NZ: 'NZ', NZL: 'NZ',
  // Australia
  AU: 'AU', AUS: 'AU',
  // European Union member states
  DE: 'EU', FR: 'EU', IT: 'EU', ES: 'EU', NL: 'EU', BE: 'EU',
  AT: 'EU', PT: 'EU', FI: 'EU', IE: 'EU', SE: 'EU', DK: 'EU',
  PL: 'EU', CZ: 'EU', HU: 'EU', RO: 'EU', GR: 'EU', SK: 'EU',
  HR: 'EU', SI: 'EU', EE: 'EU', LV: 'EU', LT: 'EU', LU: 'EU',
  MT: 'EU', CY: 'EU', BG: 'EU',
}

export function parseRegion(input: unknown): PricingRegion | null {
  const upper = String(input ?? '').trim().toUpperCase()
  if (upper === 'ZA' || upper === 'UK' || upper === 'EU' || upper === 'AU' || upper === 'NZ') {
    return upper as PricingRegion
  }
  return COUNTRY_CODE_MAP[upper] ?? null
}

export function normalizeRegion(input: unknown, fallback: PricingRegion = 'ZA'): PricingRegion {
  const parsed = parseRegion(input)
  if (!parsed) {
    if (typeof console !== 'undefined') {
      console.warn('[billing-region] Unsupported region, using fallback', { value: input, fallback })
    }
    return fallback
  }
  return parsed
}

export function getPricingRegion(countryCode: string): PricingRegion {
  return normalizeRegion(countryCode)
}

export function getRegionConfig(region: unknown): RegionConfig {
  return REGION_CONFIGS[normalizeRegion(region)]
}

export function getLocalizedPlanPrices(region: unknown): PlanPrices {
  return REGION_PLAN_PRICES[normalizeRegion(region)]
}

export function getTaxLabel(region: unknown): string {
  return getRegionConfig(region).taxLabel
}

export function getTaxShort(region: unknown): string {
  return getRegionConfig(region).taxShort
}

export function getCurrencyCode(region: unknown): string {
  return getRegionConfig(region).currencyCode
}

export function getCurrencySymbol(region: unknown): string {
  return getRegionConfig(region).currencySymbol
}

export function formatLocalPrice(amount: number, region: unknown): string {
  const config = getRegionConfig(region)
  if (amount === 0) return `${config.currencySymbol}0`
  return `${config.currencySymbol}${amount.toLocaleString(config.locale)}`
}
